import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import HttpService from '../../services/httpService';
import { setLoading, setSnackInfo } from "../../redux/actions/appAction";
import { Button, Paper, Tooltip, TablePagination, Dialog, DialogContent, DialogActions, TextField, MenuItem, Autocomplete, Typography, Card, CardContent, IconButton, CircularProgress, Box } from '@mui/material';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import LinearProgress from '@material-ui/core/LinearProgress';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import XLSX from 'xlsx';
import { useSelector } from 'react-redux';
import { ROLENAME } from '../../utils/index';
import { Link } from 'react-router-dom';
import CdrMaskedPhone from '../../components/CdrMaskedPhone';
// Material icons throughout instead of the Ant Design outlined set. The two
// families have different stroke weights and corner radii, so mixing them made
// the page look assembled from spare parts; Material also matches every other
// icon already on this screen (LocalOffer, the MUI controls, the pagination).
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import TuneIcon from "@material-ui/icons/Tune";
import DateRangeOutlinedIcon from "@material-ui/icons/DateRangeOutlined";
import GetAppIcon from "@material-ui/icons/GetApp";
import InboxOutlinedIcon from "@material-ui/icons/InboxOutlined";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import SearchIcon from "@material-ui/icons/Search";
import ClearAllIcon from "@material-ui/icons/ClearAll";
import PlayArrowRoundedIcon from "@material-ui/icons/PlayArrowRounded";
import PauseRoundedIcon from "@material-ui/icons/PauseRounded";
import RefreshIcon from "@material-ui/icons/Refresh";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import CallMadeRoundedIcon from "@material-ui/icons/CallMadeRounded";
import CallReceivedRoundedIcon from "@material-ui/icons/CallReceivedRounded";
import PhoneMissedOutlinedIcon from "@material-ui/icons/PhoneMissedOutlined";

// Brand orange - sampled from the actual Decorpot logo, same palette used
// across the rest of the app's recently-redesigned pages.
const ORANGE = "#F47E44";
const ORANGE_DARK = "#c1531f";
const ORANGE_LIGHT = "#fff1ea";
const HEADER_GRADIENT = `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 60%, #ff9c66 100%)`;
const GREEN_GRADIENT = `linear-gradient(135deg, #146c43, #1e8449)`;
// Matches the "Silka bold" heading treatment used on the lead details
// (manageProjectLog) page, so headings here read consistently with the rest
// of the app. Body text already inherits "Silka regular" globally via App.css.
const HEADING_FONT = "'Silka bold', 'Silka semiBold', sans-serif !important";

// Passed via the `sx` prop (not a makeStyles className) - these IconButtons
// are MUI v5 components, and a v4 JSS className can lose out to MUI v5's own
// emotion-injected default styles depending on injection order (confirmed on
// the untaggedLogs page), which is why `sx` is used here instead.
const ICON_BTN_SX = {
    color: ORANGE_DARK,
    backgroundColor: ORANGE_LIGHT,
    transition: "transform .15s ease, background-color .15s ease",
    "&:hover": { backgroundColor: "#ffe3d2", transform: "scale(1.08)" },
};

// Indeterminate bar shown while a fetch runs. Matches the heading gradient so
// the two read as the same system.
const BrandLinearProgress = withStyles(() => ({
    root: { height: 4, borderRadius: 999, backgroundColor: "rgba(244,126,68,0.16)" },
    bar: { borderRadius: 999, background: `linear-gradient(90deg, ${ORANGE_DARK}, ${ORANGE}, #ff9c66)` },
}))(LinearProgress);

const useStyles = makeStyles(() => ({
    page: { padding: 20, background: "#f6f7fb", minHeight: "100vh" },
    paperContent: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'transparent',
        boxShadow: 'none',
    },
    headingCard: {
        marginBottom: 20,
        borderRadius: 20,
        background: HEADER_GRADIENT,
        color: "#fff",
        boxShadow: "0 10px 28px rgba(196,83,31,0.2)",
    },
    headingWrapper: {
        display: "flex",
        alignItems: "center",
        gap: 16,
    },
    headerIconWrap: {
        width: 52,
        height: 52,
        borderRadius: "50%",
        background: "rgba(255,255,255,0.22)",
        border: "2px solid rgba(255,255,255,0.35)",
        boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    statChip: {
        background: "rgba(255,255,255,0.2)",
        color: "#fff",
        borderRadius: 20,
        padding: "7px 16px",
        fontWeight: 700,
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        gap: 6,
        whiteSpace: "nowrap",
    },
    heading: {
        fontWeight: 700,
        color: "#fff",
        fontFamily: HEADING_FONT,
    },
    filterCard: {
        borderRadius: 20,
        boxShadow: "0 6px 20px rgba(17,24,39,0.06)",
        padding: "18px 22px",
        marginBottom: 20,
        background: "#fff",
    },
    sectionLabel: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        color: "#6b7280",
        fontSize: 12.5,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.4,
        marginBottom: 10,
    },
    filterRow: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 14,
        transition: "opacity 0.2s ease",
    },
    sectionLabelRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 8,
        marginBottom: 10,
    },
    field: {
        '& .MuiOutlinedInput-root': { borderRadius: 10 },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ORANGE },
        '& .MuiInputLabel-root.Mui-focused': { color: ORANGE_DARK },
    },
    submitBtn: {
        color: "#fff !important",
        background: `linear-gradient(135deg, ${ORANGE_DARK}, ${ORANGE}) !important`,
        textTransform: "none",
        fontWeight: 600,
        borderRadius: 12,
        boxShadow: "none",
    },
    downloadBtn: {
        color: "#fff !important",
        background: `${GREEN_GRADIENT} !important`,
        textTransform: "none",
        fontWeight: 600,
        borderRadius: 12,
        boxShadow: "none",
    },
    clearBtn: {
        color: "#6b7280 !important",
        border: "1.5px solid #d8dee8 !important",
        background: "#fff !important",
        textTransform: "none",
        fontWeight: 600,
        borderRadius: 12,
    },
    tableCard: { borderRadius: 20, overflow: "hidden", boxShadow: "0 6px 20px rgba(17,24,39,0.06)", background: "#fff" },
    tableWrapper: {
        overflow: "auto",
        maxHeight: 750,
    },
    tableHeaderCell: {
        // Solid, not a gradient: across a wide table the gradient's light end
        // left the right-hand headers washed out (white on #ff9c66 is only
        // ~2:1), and a flat band keeps every label at 4.64:1.
        background: ORANGE_DARK,
        color: '#ffffff',
        fontWeight: 700,
        fontSize: 11.5,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        padding: '12px 10px',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        position: 'sticky',
        top: 0,
        zIndex: 2,
        borderBottom: 'none',
    },
    bodyCell: {
        padding: '10px',
        textAlign: 'center',
        fontSize: 13.5,
        color: "#2b3648",
        borderBottom: '1px solid #f1f2f5',
    },
    rankCell: { color: "#9aa1ac", fontWeight: 800, fontVariantNumeric: "tabular-nums" },
    // Identity, not decoration: the same person appears on many rows, and an
    // initial makes a repeated name findable while scanning.
    userCell: { display: "flex", alignItems: "center", justifyContent: "center", gap: 8 },
    userAvatar: {
        width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: ORANGE_LIGHT, color: ORANGE_DARK,
        fontSize: 11, fontWeight: 800,
    },
    typeChip: {
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "3px 10px", borderRadius: 999,
        fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap",
    },
    // A duration of 0 means the call never connected, which is the most useful
    // fact on the row - previously rendered as a bare "0s".
    durationPill: {
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "3px 10px", borderRadius: 999,
        fontSize: 12, fontWeight: 700, fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
    },
    dateStack: { lineHeight: 1.35 },
    dateMain: { fontSize: 12.5, fontWeight: 700, color: "#2b3648", whiteSpace: "nowrap" },
    dateSub: { fontSize: 11, color: "#9aa1ac", whiteSpace: "nowrap" },
    headerActions: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
    "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
    "@keyframes pulseDot": {
        "0%, 100%": { opacity: 1, transform: "scale(1)" },
        "50%": { opacity: 0.45, transform: "scale(0.82)" },
    },
    // Solid white with a dark ring: the ring defines the EDGE, since white on
    // the bright #ff9c66 end of the gradient is only about 2:1. The word
    // "Refresh" is what makes the control findable at all.
    refreshButton: {
        display: "inline-flex", alignItems: "center", gap: 7,
        padding: "8px 16px", borderRadius: 999, border: "none",
        background: "#fff", color: ORANGE_DARK,
        fontSize: 12.5, fontWeight: 800, letterSpacing: 0.2, fontFamily: "inherit",
        cursor: "pointer",
        boxShadow: "0 0 0 1px rgba(0,0,0,0.14), 0 3px 10px rgba(0,0,0,0.24)",
        transition: "background .18s ease, transform .18s ease, box-shadow .18s ease",
        "&:hover:not(:disabled)": {
            background: ORANGE_LIGHT, transform: "translateY(-1px)",
            boxShadow: "0 0 0 1px rgba(0,0,0,0.18), 0 6px 16px rgba(0,0,0,0.3)",
        },
        "&:active:not(:disabled)": { transform: "translateY(0)" },
        "&:disabled": { cursor: "wait", opacity: 0.85 },
    },
    refreshSpinning: { animation: "$spin 0.9s linear infinite" },
    lastUpdatedText: {
        display: "inline-flex", alignItems: "center", gap: 6,
        fontSize: 11, fontWeight: 600, color: "#fff",
        background: "rgba(0,0,0,0.42)", padding: "3px 10px", borderRadius: 999,
        whiteSpace: "nowrap",
    },
    liveDot: {
        width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
        background: "#FFD2B5", animation: "$pulseDot 2s ease-in-out infinite",
    },
    // Reserved slot rather than a conditional element, so nothing shifts by 4px
    // each time a fetch starts.
    progressSlot: { height: 4, borderRadius: 999, overflow: "hidden", marginBottom: 12 },
    loadingBanner: {
        display: "flex", alignItems: "center", gap: 10,
        marginBottom: 14, padding: "10px 14px", borderRadius: 12,
        background: ORANGE_LIGHT, border: "1px solid #f6d3bd",
    },
    tableRow: {
        "&:nth-of-type(odd)": { backgroundColor: "#FBFBFA" },
        "&:hover": { backgroundColor: ORANGE_LIGHT, boxShadow: `inset 3px 0 0 ${ORANGE}` },
        transition: "background-color .12s ease",
    },
    // Same treatment as the BotWot WhatsApp dashboard: a tinted pill with a
    // launch glyph, so the lead number reads as the thing you click rather
    // than as underlined text that happens to be a link.
    leadLink: {
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 9px",
        borderRadius: 7,
        background: ORANGE_LIGHT,
        color: ORANGE_DARK,
        fontWeight: 700,
        fontSize: 12.5,
        textDecoration: "none",
        cursor: "pointer",
        whiteSpace: "nowrap",
        transition: "background-color .15s ease",
        "&:hover": { background: "#ffe3d2", textDecoration: "none" },
    },
    recordingCell: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
    },
    emptyState: { padding: "60px 20px", textAlign: "center", color: "#9aa1ac" },
    emptyIconWrap: {
        width: 80,
        height: 80,
        borderRadius: "50%",
        background: ORANGE_LIGHT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 14px",
    },
    dialogHeader: {
        background: HEADER_GRADIENT,
        color: "#fff",
        padding: "18px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
    },
    dialogHeaderTitle: { display: "flex", alignItems: "center", gap: 10 },
    dialogCloseBtn: {
        color: "rgba(255,255,255,0.9)",
        "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
    },
}));

const CallRecordingsTagged = (props) => {
    const classes = useStyles();
    const { userDetails } = useSelector((state) => state.user);
    const [callLogsData, setCallLogsData] = useState([]);
    const [callLogsCount, setCallLogsCount] = useState(0)
    const [showTagged, setShowTagged] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [csvData, setCsvData] = useState([]);
    const buttonRef = useRef(null);
    const [inputValues, setInputValues] = useState({});
    const [showSubmitMap, setShowSubmitMap] = useState({});
    const [confirmation, setConfirmation] = useState(false);
    const [rowId, setRowId] = useState("");
    const [storeDateFilter, setPivotDateFilters] = useState({ startDate: "", endDate: "" })
    const [selectedOption, setSelectedOption] = useState("All");
    const [selectedCallStatus, setSelectedCallStatus] = useState("Both");
    const [maxWidth, setMaxWidth] = useState(150); // Initial width
    const [searchInput, setSearchInput] = useState("")
    const [selectedSearchType, setSelectedSearchType] = useState("");
    const [users, setUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const searchTypes = ['Customer Name', 'Email ID', 'Lead No', 'Project No', 'Contact No', 'Unique Lead No'];
    const options = ["All", "Outgoing", "Incoming", "Missed"];
    const callStatus = ["Both", "Answered", "Missed"];
    // Local, in-table/button loaders instead of relying solely on the
    // app-wide Backdrop, so filtering/searching give immediate, visible
    // feedback right where the click happened.
    const [dataLoading, setDataLoading] = useState(false);
    // When the rows on screen were last pulled, so the header can say how stale
    // they are. Only set on a successful fetch - a failed refresh must not make
    // old rows look fresh.
    const [lastUpdated, setLastUpdated] = useState(null);
    // "Updated 4 minutes ago" is only true at the instant it renders, so a slow
    // tick re-renders it. 30s is plenty - moment rounds to the minute anyway.
    const [nowTick, setNowTick] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNowTick(Date.now()), 30000);
        return () => clearInterval(id);
    }, []);
    const [submittingTag, setSubmittingTag] = useState(false);
    // Tracks which row's recording is currently playing (only one at a
    // time) - drives the custom play/pause icon and pauses whichever else
    // was playing before starting a new one.
    const [playingId, setPlayingId] = useState(null);
    const audioRefs = useRef({});

    const togglePlay = (logId) => {
        const audio = audioRefs.current[logId];
        if (!audio) return;

        if (playingId === logId) {
            audio.pause();
            setPlayingId(null);
            return;
        }

        Object.entries(audioRefs.current).forEach(([id, el]) => {
            if (id !== logId && el) el.pause();
        });

        audio.play();
        setPlayingId(logId);
    };

    const callTypeMap = {
        incoming: 'Incoming',
        outgoing: 'Outgoing',
        missed: 'Missed',
    };

    const normalizeNumber = (number) => number?.replace(/\D/g, '').slice(-10); // Utility to extract last 10 digits

    const formatDuration = (seconds) => {
        if (seconds == null) return 'NaN';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}m ${secs}s`;
    };

    // Call type as a tinted chip with a directional icon. Three near-identical
    // words in a column of plain text were impossible to scan; direction is now
    // carried by shape and colour as well as the label.
    const typeChipFor = (type) => {
        const label = callTypeMap[type] ?? "Type Unknown";
        const l = String(label).toLowerCase();
        if (l.includes("outgoing")) {
            return { label, bg: "#eef2f8", fg: "#2a4d7a", icon: <CallMadeRoundedIcon style={{ fontSize: 12 }} /> };
        }
        if (l.includes("incoming")) {
            return { label, bg: "#e8f6ef", fg: "#17734f", icon: <CallReceivedRoundedIcon style={{ fontSize: 12 }} /> };
        }
        if (l.includes("missed")) {
            return { label, bg: "#fdecea", fg: "#b03a2e", icon: <PhoneMissedOutlinedIcon style={{ fontSize: 12 }} /> };
        }
        return { label, bg: "#f1f2f5", fg: "#8a8984", icon: null };
    };

    const initialsOf = (name = "") =>
        String(name).trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

    const storePivotDate = (e) => {
        const { name, value } = e.target
        setPivotDateFilters({ ...storeDateFilter, [name]: value })
    }

    const headers = [
        { key: "leadId.lead_no", label: "Lead No" },
        { key: "user.name", label: "ERP User" },
        { key: "userPhone", label: "Caller Number" },
        // { key: "phoneNumber", label: "Destination Number" },
        { key: 'type', label: "Call Type" },
        { key: "duration", label: "Duration" },
        // { key: "overallCallStatus", label: "Overall Call Status" },
        { key: "recordingUrl", label: "Recordings" },
    ];

    useEffect(() => {
        retrieveCallLogs();
        getUsersCDR();
        // eslint-disable-next-line
    }, []);

    const retrieveCallLogs = async (selectedOption, selectedCallStatus, usersData, selectedSearchType, searchInput, page = 0, rowsPerPage = 100) => {
        setDataLoading(true);
        let usersList = [];
        if ((usersData?.users && usersData?.users.length !== 0) || (selectedUsers && selectedUsers.length !== 0)) {
            usersList = usersData?.users?.map((el) => el._id) || selectedUsers?.map((el) => el._id)
        }
        try {
            let params = {
                startDate: storeDateFilter.startDate,
                endDate: storeDateFilter.endDate,
                callType: selectedOption || "All",
                overallCallStatus: selectedCallStatus || "Both",
                users: usersList.length !== 0 ? usersList : [],
                searchType: selectedSearchType ? selectedSearchType : "",
                search: searchInput ? searchInput.toUpperCase() : "",
                page: page,
                rowsPerPage: rowsPerPage
            };
            const response = await HttpService.getCallLogsTagged(params);
            setCallLogsData(response?.data.data);
            setCallLogsCount(response?.data.total)
            setCsvData(response?.data.data);
            // Inside the try, after the assignments: a fetch that threw leaves
            // the previous timestamp alone rather than vouching for stale rows.
            setLastUpdated(new Date());
        } catch (error) {
            console.error("Error while fetching call logs", error);
        }
        setDataLoading(false);
    };

    const getUsersCDR = async () => {
        try {
            const response = await HttpService.getUsersCDR();
            setUsers(response?.data)
        } catch (error) {
            console.error("Error while fetching users", error)
        }
    }
    // const handleToggleChange = () => {
    //     setShowTagged(!showTagged);
    //     setSelectedOption("Both")
    //     setSelectedCallStatus("Both")
    //     setInputValues({})
    //     setShowSubmitMap({})
    // };

    const handleDownloadExcel = () => {
        if (!callLogsData || callLogsData.length === 0) {
            console.warn("No data available to download.");
            return;
        }

        const data = callLogsData.map((row, i) => ({
            "Sl. No.": i + 1,
            "Lead No": row?.leadId?.lead_no || "N/A",
            "ERP User": row?.user?.name || "N/A",
            "Role": row?.rolesData?.[0]?.name || "N/A",
            "Call Type": callTypeMap[row?.type] || "N/A",
            "Caller Number": row?.userPhone || "N/A",
            // "Destination Number": row?.phoneNumber || "N/A",
            "Call Duration (sec)": row?.duration ? formatDuration(row.duration) : "NaN",
            "Recordings": (row?.recordingUrl) ? "Download" : "No Recording",
            "Date": row?.startTime ? moment(row.startTime).format("DD-MM-YYYY") : "N/A",
            // "Overall Call Status": row?.overallCallStatus ? row.overallCallStatus : "NA"
        }));

        const ws = XLSX.utils.json_to_sheet(data);

        // Add hyperlinks to recordings
        const callRecordingCol = "I"; // 9th column = 'I'
        callLogsData.forEach((row, i) => {
            const cellRef = `${callRecordingCol}${i + 2}`;
            if (row.recordingUrl) {
                ws[cellRef] = {
                    t: "s",
                    v: "Download",
                    l: { Target: row.recordingUrl }, // Hyperlink target
                };
            } else {
                ws[cellRef] = { t: "s", v: "No Recording" };
            }
        });

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "CDR Logs");

        XLSX.writeFile(wb, `Tagged_CDR_Logs_${moment().format("YYYYMMDD_HHmmss")}.xlsx`);
    };
    const downloadExcel = () => {
        props.setLoading(true);
        try {
            setTimeout(() => {
                buttonRef.current.link.click();
            });
        } catch (err) {
            console.error(err.message);
        }
        props.setLoading(false);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        retrieveCallLogs(selectedOption, selectedCallStatus, selectedUsers, selectedSearchType, searchInput, newPage, rowsPerPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        retrieveCallLogs(selectedOption, selectedCallStatus, selectedUsers, selectedSearchType, searchInput, 0, newRowsPerPage);
    };

    const handleInputChange = (logId, value) => {
        setInputValues((prev) => ({ ...prev, [logId]: value }));
        setShowSubmitMap((prev) => ({ ...prev, [logId]: value.trim() !== "" }));
    };

    const handleConfirmation = (id) => {
        setConfirmation(true)
        setRowId(id)
    };

    const handleSubmit = async (rowId) => {
        try {
            let payload = { cdr: rowId };

            const inputValueForRow = inputValues[rowId]?.trim();
            if (!inputValueForRow) {
                props.setSnackInfo("Please enter lead info before submitting.", "warning");
                return;
            }

            setSubmittingTag(true);

            if (/^\d+\.1$/.test(inputValueForRow)) {
                payload.text = Number(inputValueForRow);
            } else {
                payload.text = inputValueForRow;
            }

            const response = await HttpService.updateLeadInfoCDR(payload);
            if (response.data.type === "warning") {
                props.setSnackInfo(response.data.message, "warning");
            } else if (response.data.type === "success") {
                props.setSnackInfo(response.data.message, "success");
            }

            // Clear input and button visibility after submit
            setInputValues((prev) => ({ ...prev, [rowId]: "" }));
            setShowSubmitMap((prev) => ({ ...prev, [rowId]: false }));

            retrieveCallLogs();
        } catch (error) {
            console.error("Error ->", error);
        }
        setSubmittingTag(false);
        setConfirmation(false);
    };

    const handlePivotDateFilter = async () => {
        retrieveCallLogs(selectedOption, selectedCallStatus, selectedUsers, selectedSearchType, searchInput)
    }

    const handleSearchType = (value) => {
        setSelectedSearchType(value)
        setSearchInput('')
    }
    const handleSearch = async (value) => {
        await retrieveCallLogs(selectedOption, selectedCallStatus, selectedUsers, selectedSearchType, searchInput)
    }

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && selectedSearchType && searchInput) {
            handleSearch(searchInput.toUpperCase());
        }
    };

    const clearFilter = () => {
        setSearchInput("")
        setSelectedSearchType("")
        retrieveCallLogs(selectedOption, selectedCallStatus, selectedUsers, "", "")
    }

    const handleUserChange = async (event, newValue) => {
        setSelectedUsers(newValue);
        handleUsersData(newValue);
    };

    const handleUsersData = async (newValue) => {
        let obj = {
            users: newValue
        }
        await retrieveCallLogs(selectedOption, selectedCallStatus, obj, selectedSearchType, searchInput)
    }

    const handleChange = async (event) => {
        const value = event.target.value;
        setSelectedOption(value);
        await retrieveCallLogs(event.target.value, selectedCallStatus, selectedUsers, selectedSearchType, searchInput);
    };

    const handleCallStatusChange = async (event) => {
        const value = event.target.value;
        setSelectedCallStatus(value);
        await retrieveCallLogs(selectedOption, event.target.value, selectedUsers, selectedSearchType, searchInput);
    };

    const formatDataForCSV = (data) => {
        return data.map((item) => {
            return {
                "Lead No": item?.leadId?.lead_no || "NA",
                "Caller Number": item?.callerNumber || "NA",
                // "Destination Number": item?.destinationNumber || "NA",
                "Call Type": callTypeMap[item?.callType] || "NA",
                "Duration": item?.conversationDuration || "NA",
                "Recordings": item?.recordingURL || "No Records",
            };
        });
    };

    const canSelectUsers = userDetails?.roles && !userDetails.roles.some(role => [ROLENAME.SalesUser, ROLENAME.DesignUser].includes(role.name));

    return (
        <div className={classes.page}>
            <Paper className={classes.paperContent}>
                <Card className={classes.headingCard}>
                    <CardContent>
                        <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
                            <div className={classes.headingWrapper}>
                                <div className={classes.headerIconWrap}>
                                    <LocalOfferIcon style={{ fontSize: 26, color: "#fff" }} />
                                </div>
                                <div>
                                    <Typography
                                        variant="h5"
                                        className={classes.heading}
                                    >
                                        Tagged Call Detail Recordings
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        style={{
                                            color: "rgba(255,255,255,0.85)",
                                            marginTop: 4,
                                            fontSize: 13
                                        }}
                                    >
                                        Showing last 15 days data by default. Use date filters to view custom data.
                                    </Typography>
                                </div>
                            </div>

                            <div className={classes.headerActions}>
                                <div className={classes.statChip}>
                                    <LocalOfferIcon style={{ fontSize: 15 }} />
                                    {callLogsCount} Tagged Call{callLogsCount === 1 ? "" : "s"}
                                </div>
                                <Tooltip
                                    title={
                                        lastUpdated
                                            ? `Last pulled at ${moment(lastUpdated).format("DD MMM YYYY, hh:mm:ss A")} — click to fetch the latest logs`
                                            : "Fetch the latest logs"
                                    }
                                >
                                    {/* A labelled button rather than an IconButton:
                                        the word "Refresh" is what makes the control
                                        findable, and a tooltip alone only helps
                                        people who already hovered. The span is
                                        required because a disabled button fires no
                                        mouse events, so the tooltip would die
                                        exactly mid-refresh. */}
                                    <span style={{ display: "inline-flex" }}>
                                        <button
                                            type="button"
                                            className={classes.refreshButton}
                                            onClick={() => retrieveCallLogs(selectedOption, selectedCallStatus, { users: selectedUsers }, selectedSearchType, searchInput, page, rowsPerPage)}
                                            disabled={dataLoading}
                                        >
                                            <RefreshIcon
                                                className={dataLoading ? classes.refreshSpinning : undefined}
                                                style={{ fontSize: 17 }}
                                            />
                                            {dataLoading ? "Refreshing…" : "Refresh"}
                                        </button>
                                    </span>
                                </Tooltip>
                                {lastUpdated && (
                                    <div className={classes.lastUpdatedText}>
                                        <span className={classes.liveDot} />
                                        {/* Relative beats a clock time: it answers
                                            "is this stale?" without the reader doing
                                            the arithmetic. */}
                                        {dataLoading ? "Fetching latest…" : `Updated ${moment(lastUpdated).from(nowTick)}`}
                                    </div>
                                )}
                            </div>
                        </Box>
                    </CardContent>
                </Card>

                {/* Reserved slot, so starting a fetch does not jog the page. */}
                <div className={classes.progressSlot}>
                    {dataLoading ? <BrandLinearProgress /> : null}
                </div>

                {/* The bar above shows that something is happening; this says
                    what, the way the call report does. */}
                {dataLoading && (
                    <div className={classes.loadingBanner}>
                        <CircularProgress size={17} thickness={5} style={{ color: ORANGE_DARK }} />
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: ORANGE_DARK }}>
                                Fetching tagged call logs…
                            </div>
                            <div style={{ fontSize: 11.5, color: "#7a6a60" }}>
                                Pulling every tagged recording for this selection — a wide date range can take a few seconds.
                            </div>
                        </div>
                    </div>
                )}

                <Box className={classes.filterCard}>
                    {/* No loading badge here: the bar and banner above already
                        say a fetch is running, and a third copy of the same
                        message inside the filter card was just noise. */}
                    <Box className={classes.sectionLabelRow}>
                        <Typography className={classes.sectionLabel} style={{ marginBottom: 0 }}>
                            <TuneIcon style={{ fontSize: 15 }} />
                            Filters
                        </Typography>
                    </Box>
                    <Box
                        className={classes.filterRow}
                        style={{ marginBottom: 18, opacity: dataLoading ? 0.55 : 1, pointerEvents: dataLoading ? "none" : "auto" }}
                    >
                        <TextField
                            select
                            label="Call Type"
                            variant="outlined"
                            size="small"
                            value={selectedOption}
                            onChange={handleChange}
                            className={classes.field}
                            style={{ minWidth: 150 }}
                            disabled={dataLoading}
                        >
                            {options.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </TextField>

                        {/* <TextField
                            select
                            label="Overall Call Status"
                            variant="outlined"
                            size="small"
                            value={selectedCallStatus}
                            onChange={handleCallStatusChange}
                            fullWidth
                        >
                            {callStatus.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </TextField> */}

                        {canSelectUsers && (
                            <Autocomplete
                                multiple
                                disabled={dataLoading}
                                options={users}
                                getOptionLabel={(option) => option.name}
                                value={selectedUsers}
                                onChange={handleUserChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        size="small"
                                        label="Select User"
                                        className={classes.field}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <PersonOutlineIcon style={{ fontSize: 16, color: "#9aa1ac", marginRight: 4 }} />
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                style={{ minWidth: 220 }}
                            />
                        )}

                        <TextField
                            id="startDate"
                            name="startDate"
                            label="From Date"
                            type="date"
                            variant="outlined"
                            size="small"
                            value={storeDateFilter.startDate}
                            onChange={storePivotDate}
                            InputLabelProps={{ shrink: true }}
                            className={classes.field}
                            style={{ width: 175 }}
                            disabled={dataLoading}
                        />

                        <TextField
                            id="endDate"
                            name="endDate"
                            label="End Date"
                            type="date"
                            variant="outlined"
                            size="small"
                            value={storeDateFilter.endDate}
                            onChange={storePivotDate}
                            InputLabelProps={{ shrink: true }}
                            className={classes.field}
                            style={{ width: 175 }}
                            disabled={dataLoading}
                        />

                        <Button
                            variant="contained"
                            onClick={handlePivotDateFilter}
                            disabled={dataLoading}
                            className={classes.submitBtn}
                            startIcon={dataLoading ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : <DateRangeOutlinedIcon />}
                        >
                            {dataLoading ? "Loading..." : "Apply Date Filter"}
                        </Button>

                        <Box style={{ flexGrow: 1 }} />

                        <Tooltip title={callLogsData?.length ? "Download these tagged logs as an Excel file" : "Nothing to download for this selection"}>
                            <span>
                                <Button
                                    onClick={handleDownloadExcel}
                                    variant="contained"
                                    className={classes.downloadBtn}
                                    startIcon={dataLoading ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : <GetAppIcon />}
                                    disabled={dataLoading || !callLogsData?.length}
                                >
                                    {dataLoading ? "Loading…" : "Download Excel"}
                                </Button>
                            </span>
                        </Tooltip>

                        <CSVLink
                            headers={headers}
                            data={csvData}
                            ref={buttonRef}
                            filename="call_recordings.csv"
                            style={{ display: 'none' }}
                        />
                    </Box>

                    <Typography className={classes.sectionLabel}>
                        <SearchIcon style={{ fontSize: 15 }} />
                        Search
                    </Typography>
                    <Box
                        className={classes.filterRow}
                        style={{ opacity: dataLoading ? 0.55 : 1, pointerEvents: dataLoading ? "none" : "auto" }}
                    >
                        <TextField
                            select
                            value={selectedSearchType}
                            label="Search Type"
                            name="searchType"
                            onChange={(e) => handleSearchType(e.target.value)}
                            variant="outlined"
                            size="small"
                            className={classes.field}
                            style={{ minWidth: 220 }}
                            disabled={dataLoading}
                        >
                            {searchTypes?.map((el, index) => (
                                <MenuItem key={index} value={el}>{el}</MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label="Enter the input"
                            name="searched"
                            size="small"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            variant="outlined"
                            className={classes.field}
                            style={{ flex: "1 1 320px" }}
                            disabled={dataLoading}
                        />

                        <Button
                            disabled={!selectedSearchType || !searchInput || dataLoading}
                            variant="contained"
                            onClick={() => handleSearch(searchInput.toUpperCase())}
                            className={classes.submitBtn}
                            startIcon={dataLoading ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : <SearchIcon />}
                        >
                            {dataLoading ? "Loading..." : "Search"}
                        </Button>

                        <Button
                            variant="outlined"
                            onClick={clearFilter}
                            disabled={dataLoading}
                            className={classes.clearBtn}
                            startIcon={<ClearAllIcon />}
                        >
                            Clear Filter
                        </Button>
                    </Box>
                </Box>

                <Box className={classes.tableCard}>
                    {dataLoading ? (
                        <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 0" }}>
                            <CircularProgress style={{ color: ORANGE }} />
                            <Typography style={{ color: "#9aa1ac", marginTop: 10 }}>Loading call logs...</Typography>
                        </Box>
                    ) : !callLogsData?.length ? (
                        <Box className={classes.emptyState}>
                            <Box className={classes.emptyIconWrap}>
                                <InboxOutlinedIcon style={{ fontSize: 36, color: ORANGE_DARK }} />
                            </Box>
                            <Typography style={{ fontWeight: 600, color: "#7d8794" }}>
                                No tagged call logs found
                            </Typography>
                            <Typography variant="body2" style={{ color: "#a4abb5", marginTop: 4 }}>
                                Try adjusting your filters or date range.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <div className={classes.tableWrapper}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr>
                                            <th className={classes.tableHeaderCell}>Sl No.</th>
                                            <th className={classes.tableHeaderCell}>Lead No.</th>
                                            <th className={classes.tableHeaderCell}>Client Name</th>
                                            <th className={classes.tableHeaderCell}>ERP User</th>
                                            <th className={classes.tableHeaderCell}>Role</th>
                                            <th className={classes.tableHeaderCell}>Call Type</th>
                                            <th className={classes.tableHeaderCell}>Caller Number</th>
                                            <th className={classes.tableHeaderCell}>Destination Number</th>
                                            <th className={classes.tableHeaderCell}>Call Duration</th>
                                            {/* <th className={classes.tableHeaderCell}>Overall Call Status</th> */}
                                            <th className={classes.tableHeaderCell}>Recordings</th>
                                            <th className={classes.tableHeaderCell}>Date</th>
                                            <th className={classes.tableHeaderCell}>Time</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {callLogsData?.map((log, index) => (
                                            <tr key={index} className={classes.tableRow}>
                                                <td className={`${classes.bodyCell} ${classes.rankCell}`}>{page * rowsPerPage + index + 1}</td>
                                                <td className={classes.bodyCell}>
                                                    {
                                                        log.leadId?.lead_no ?
                                                            <Tooltip title={`Open lead ${log.leadId.lead_no} in a new tab`}>
                                                                <Link to={`/project-logs/${log.leadId?._id}`} target="_blank" rel="noopener noreferrer" className={classes.leadLink}>
                                                                    {log.leadId?.lead_no}
                                                                    <OpenInNewIcon style={{ fontSize: 11 }} />
                                                                </Link>
                                                            </Tooltip> :
                                                            <span style={{ color: "#9aa1ac" }}>NA</span>
                                                    }
                                                </td>
                                                <td className={classes.bodyCell}>
                                                    {log.customer?.name
                                                        ? <strong style={{ fontWeight: 600 }}>{log.customer.name}</strong>
                                                        : <span style={{ color: "#9aa1ac" }}>NA</span>}
                                                </td>
                                                <td className={classes.bodyCell}>
                                                    {log.user?.name ? (
                                                        <div className={classes.userCell}>
                                                            <span className={classes.userAvatar}>{initialsOf(log.user.name)}</span>
                                                            <strong style={{ fontWeight: 600 }}>{log.user.name}</strong>
                                                        </div>
                                                    ) : <span style={{ color: "#9aa1ac" }}>NA</span>}
                                                </td>
                                                <td className={classes.bodyCell}>
                                                    {log.rolesData[0] ? log.rolesData[0].name : 'NA'}
                                                </td>
                                                <td className={classes.bodyCell}>
                                                    {(() => {
                                                        const t = typeChipFor(log.type);
                                                        return (
                                                            <span className={classes.typeChip} style={{ background: t.bg, color: t.fg }}>
                                                                {t.icon}{t.label}
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                                <td className={classes.bodyCell}>
                                                    <CdrMaskedPhone
                                                        cdrId={log._id}
                                                        field="userPhone"
                                                        maskedValue={log.userPhone || 'NA'}
                                                        setSnackInfo={props.setSnackInfo}
                                                    />
                                                </td>
                                                <td className={classes.bodyCell}>
                                                    <CdrMaskedPhone
                                                        cdrId={log._id}
                                                        field="phoneNumber"
                                                        maskedValue={log.phoneNumber || 'NA'}
                                                        setSnackInfo={props.setSnackInfo}
                                                    />
                                                </td>
                                                <td className={classes.bodyCell}>
                                                    <Tooltip title={Number(log.duration) > 0 ? "Call connected" : "Call did not connect"}>
                                                        <span
                                                            className={classes.durationPill}
                                                            style={Number(log.duration) > 0
                                                                ? { background: "#e8f6ef", color: "#17734f" }
                                                                : { background: "#f1f2f5", color: "#9aa1ac" }}
                                                        >
                                                            {Number(log.duration) > 0 ? formatDuration(log.duration) : "Not connected"}
                                                        </span>
                                                    </Tooltip>
                                                </td>
                                                {/* <td className={classes.bodyCell}>
                                                    {log.overallCallStatus ?? 'Status Unknown'}
                                                </td> */}
                                                <td className={classes.bodyCell}>
                                                    {(log.recordingUrl) ? (
                                                        <div className={classes.recordingCell}>
                                                            <Tooltip title={playingId === log._id ? "Pause recording" : "Play recording"}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => togglePlay(log._id)}
                                                                    sx={ICON_BTN_SX}
                                                                >
                                                                    {playingId === log._id ? <PauseRoundedIcon style={{ fontSize: 18 }} /> : <PlayArrowRoundedIcon style={{ fontSize: 18 }} />}
                                                                </IconButton>
                                                            </Tooltip>
                                                            <audio
                                                                ref={(el) => { audioRefs.current[log._id] = el; }}
                                                                src={log.recordingUrl}
                                                                preload="none"
                                                                onEnded={() => setPlayingId(null)}
                                                                style={{ display: "none" }}
                                                            />
                                                            <Tooltip title="Download recording">
                                                                <IconButton
                                                                    size="small"
                                                                    component="a"
                                                                    href={`${log.recordingUrl}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    sx={ICON_BTN_SX}
                                                                >
                                                                    <GetAppIcon style={{ fontSize: 16 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </div>
                                                    ) : (
                                                        <span style={{ fontSize: 13, color: "#9aa1ac" }}>No Recording</span>
                                                    )}
                                                </td>
                                                <td className={classes.bodyCell}>
                                                    <div className={classes.dateStack}>
                                                        <div className={classes.dateMain}>
                                                            {log.startTime ? moment(log.startTime).format("DD MMM YYYY") : '-'}
                                                        </div>
                                                        <div className={classes.dateSub}>
                                                            {log.startTime ? moment(log.startTime).format("dddd") : ''}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className={classes.bodyCell}>
                                                    {log.startTime ? (
                                                        <span className={classes.durationPill} style={{ background: "#f1f4f8", color: "#5c6779" }}>
                                                            <AccessTimeIcon style={{ fontSize: 12 }} />
                                                            {moment(log.startTime).format("hh:mm A")}
                                                        </span>
                                                    ) : ''}
                                                </td>
                                                {/* <td style={{ padding: '10px', borderBottom: '1px solid #ccc' }}>
                                                    {log.recordingURL ? (
                                                        <div>
                                                            <audio id={`audio-${index}`} src={log.recordingURL} />
                                                            <button
                                                                onClick={() => {
                                                                    const audio = document.getElementById(`audio-${index}`);
                                                                    audio.play();
                                                                }}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    color: 'blue',
                                                                    textDecoration: 'underline'
                                                                }}
                                                            >
                                                                ▶️ Play
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        "No Records found"
                                                    )}
                                                </td> */}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <TablePagination
                                rowsPerPageOptions={[100, 200, 400, { label: `${callLogsCount}`, value: callLogsCount }]}
                                colSpan={14}
                                count={callLogsCount}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                component="div"
                                SelectProps={{
                                    inputProps: { 'aria-label': 'rows per page' },
                                    native: true,
                                }}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </>
                    )}
                </Box>
            </Paper>

            <Dialog
                open={confirmation}
                onClose={() => !submittingTag && setConfirmation(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ style: { borderRadius: 20, overflow: "hidden" } }}
            >
                <Box className={classes.dialogHeader}>
                    <Box className={classes.dialogHeaderTitle}>
                        <CheckCircleOutlineIcon style={{ fontSize: 22 }} />
                        <Typography style={{ fontFamily: HEADING_FONT, fontSize: 17 }}>Confirm Tagging</Typography>
                    </Box>
                    <IconButton size="small" className={classes.dialogCloseBtn} onClick={() => setConfirmation(false)} disabled={submittingTag}>
                        <CloseIcon style={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
                <DialogContent style={{ paddingTop: 24, textAlign: "center" }}>
                    <Typography variant="body1" style={{ color: "#374151" }}>
                        Are you sure you want to tag this call log with the entered lead info?
                    </Typography>
                </DialogContent>
                <DialogActions style={{ justifyContent: "center", padding: "12px 24px 20px", gap: 8 }}>
                    <Button variant="outlined" className={classes.clearBtn} onClick={() => setConfirmation(false)} disabled={submittingTag}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        className={classes.submitBtn}
                        onClick={() => handleSubmit(rowId)}
                        disabled={submittingTag}
                        startIcon={submittingTag ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <CheckCircleOutlineIcon />}
                    >
                        {submittingTag ? "Submitting..." : "Submit"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default connect(null, { setLoading, setSnackInfo })(CallRecordingsTagged);
